// src/components/ExamQuestionBreakdown.jsx
// Home of BreakdownBox, the "what's expected" text box shown for a question
// - rendered inside ExamSplitScreen's "QUESTION INFORMATION" pane, and
// (default export below) as its own standalone full-screen stop between the
// Task Summary screen and the split screen. Off by default - only homework
// types with examShowBreakdownScreen enabled (Head Marker Dashboard >
// Settings > Homework) route through it; see ExamWindowApp.jsx's navState
// screens. Its content (breakdownScreenText fields) is authored separately
// from the split screen's pane (breakdown fields) but falls back to it when
// left blank, so the two can show different information for the same
// question - see updateSectionBreakdownScreenText/
// updateQuestionGroupBreakdownScreenText in HeadMarkerDashboard.jsx.

import React from 'react';
import { ChevronRight, Clock } from 'lucide-react';
import { toDisplayHtml } from '../utils/richTextDisplay';
import { calculateGroupMarks } from '../utils/examGroups';

function formatCountdown(ms) {
  if (ms <= 0) return '00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

const boxStyle = {
  background: 'white',
  border: '2px solid #e2e8f0',
  borderRadius: '8px',
  padding: '20px',
  fontSize: '14px',
  lineHeight: 1.6,
  color: '#2c3e50'
};

// text can be Tiptap HTML (current) or a legacy plain string (pre-rich-text)
// - toDisplayHtml tells the two apart. minHeight only applies when there's
// real content, so an empty fallback message doesn't look like a giant box.
// Exported so ExamSplitScreen's persistent "Question Information" side
// panel renders breakdown text identically to this pre-question screen,
// instead of duplicating (and drifting from) the same logic a third time.
export const BreakdownBox = ({ text, fallback, minHeight }) => (
  text && text.trim() ? (
    <div style={{ ...boxStyle, minHeight }} dangerouslySetInnerHTML={{ __html: toDisplayHtml(text) }} />
  ) : (
    <div style={boxStyle}>{fallback}</div>
  )
);

const ExamQuestionBreakdown = ({ group, sectionStructure, sectionNumber, totalSections, remainingMs, unlimitedTime, clockStarting, timerError, onNext }) => {
  const isUrgent = remainingMs > 0 && remainingMs < 5 * 60 * 1000;
  const totalMarks = calculateGroupMarks(group, sectionStructure);
  const isMultiPart = group.sections.length > 1;
  // Breakdown-screen text is independent of the split screen's QUESTION
  // INFORMATION pane, but falls back to that same text (breakdown/
  // breakdownScreenText) when nothing screen-specific has been authored, so
  // existing content still shows here unchanged until a head marker
  // deliberately writes something different for this screen.
  const groupScreenText = group.breakdownScreenText || group.breakdown;
  const hasGroupBreakdown = isMultiPart && !!(groupScreenText && groupScreenText.trim());

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <div style={{
        background: '#1a2332', color: 'white', padding: '14px 24px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <div>
          <div style={{ fontSize: '11px', letterSpacing: '1px', color: '#8b96a8', fontWeight: '700' }}>
            QUESTION {sectionNumber} OF {totalSections}
          </div>
          <div style={{ fontSize: '15px', fontWeight: '600' }}>{group.name}</div>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '8px 16px', borderRadius: '6px',
          background: isUrgent ? '#e74c3c' : 'rgba(255,255,255,0.1)',
          fontWeight: '700', fontSize: unlimitedTime ? '13px' : '18px', fontVariantNumeric: 'tabular-nums'
        }}>
          <Clock size={18} />
          {unlimitedTime ? 'No time limit' : clockStarting ? 'Starting timer...' : formatCountdown(remainingMs)}
        </div>
      </div>

      {timerError && (
        <div style={{ padding: '10px 24px', background: '#fee2e2', color: '#991b1b', fontSize: '13px' }}>
          {timerError}
        </div>
      )}

      <div style={{ flex: 1, padding: '40px', maxWidth: '760px', margin: '0 auto', width: '100%' }}>
        <h2 style={{ color: '#2c3e50' }}>
          {group.name}{totalMarks ? ` (${totalMarks} marks)` : ''}
        </h2>
        {hasGroupBreakdown ? (
          <BreakdownBox text={groupScreenText} minHeight="160px" />
        ) : isMultiPart ? (
          <div style={{ display: 'grid', gap: '14px' }}>
            {group.sections.map(s => {
              const inTechGroup = (sectionStructure?.technicalMarkGroups || []).some(g => g.sections.includes(s.id));
              const inProfGroup = (sectionStructure?.professionalMarkGroups || []).some(g => g.sections.includes(s.id));
              const partMarks = (inTechGroup || inProfGroup) ? null
                : (s.maxTechnical ?? s.maxMark ?? 0) + (s.maxProfessional ?? 1);
              return (
                <div key={s.id}>
                  <div style={{ fontWeight: '700', color: '#2c3e50', marginBottom: '8px' }}>
                    {s.name}{partMarks ? ` (${partMarks} marks)` : ''}
                  </div>
                  <BreakdownBox text={s.breakdownScreenText || s.breakdown} fallback="No breakdown has been provided for this part." />
                </div>
              );
            })}
          </div>
        ) : (
          <BreakdownBox
            text={group.sections[0].breakdownScreenText || group.sections[0].breakdown}
            fallback="No breakdown has been provided for this question."
            minHeight="160px"
          />
        )}
      </div>

      <div style={{ padding: '20px 40px', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onNext}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '12px 28px',
            background: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '700',
            fontSize: '15px'
          }}
        >
          Click Next to continue
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default ExamQuestionBreakdown;
